import { ReactNode, useMemo, useState } from "react"
import {
    IFullMenuCategory,
    IFullMenuImage,
    IFullMenuItem,
    IFullMenuModifier,
    IFullMenuPrice,
    IFullRestaurantMenu,
    updateRestaurantMenuItemAvailability,
    updateRestaurantMenuModifierAvailability,
} from "@/shared/api/restaurant-menus"
import notify from "@/shared/lib/notify"
import { Modal } from "@/shared/ui/modal"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import {
    RestaurantMenuEntityTarget,
    RestaurantMenuImagesTarget,
} from "../model/types"

interface MenuTreeProps {
    menuId: string
    data: IFullRestaurantMenu
    onRefresh: () => Promise<void>
    onOpenImages: (target: RestaurantMenuImagesTarget) => void
    onOpenTranslations: (target: RestaurantMenuEntityTarget) => void
}

interface CarouselState {
    title: string
    images: IFullMenuImage[]
    index: number
}

interface SearchResult {
    count: number
    categoryMatches: Set<string>
    itemMatches: Set<string>
    modifierMatches: Set<string>
    visibleCategories: Set<string>
    visibleItems: Set<string>
}

const sortImages = (images: IFullMenuImage[]): IFullMenuImage[] => {
    return [...images].sort((first, second) => (first.sort_order ?? 0) - (second.sort_order ?? 0))
}

const sortModifiers = (modifiers: IFullMenuModifier[]): IFullMenuModifier[] => {
    return [...modifiers].sort((first, second) => (first.position ?? Number.MAX_SAFE_INTEGER) - (second.position ?? Number.MAX_SAFE_INTEGER))
}

const getCategoryKey = (category: IFullMenuCategory, categoryIndex: number): string => {
    return category.id ?? category.name ?? `category-${categoryIndex}`
}

const getItemKey = (
    item: IFullMenuItem,
    categoryKey: string,
    itemIndex: number
): string => {
    return item.item_id ?? item.sku ?? `${categoryKey}:item-${itemIndex}`
}

const getModifierKey = (
    modifier: IFullMenuModifier,
    itemKey: string,
    sizeIndex: number,
    groupIndex: number,
    modifierIndex: number
): string => {
    return `${itemKey}:${sizeIndex}:${groupIndex}:${modifier.item_id ?? modifier.sku ?? `modifier-${modifierIndex}`}`
}

const normalizeSearchText = (value: string): string => value.trim().toLocaleLowerCase("ru-RU")

const isTextMatch = (value: string | undefined, normalizedQuery: string): boolean => {
    return Boolean(normalizedQuery && value?.toLocaleLowerCase("ru-RU").includes(normalizedQuery))
}

const HighlightedText = ({ text, query }: { text: string; query: string }) => {
    const normalizedQuery = normalizeSearchText(query)
    if (!normalizedQuery) {
        return <>{text}</>
    }

    const normalizedText = text.toLocaleLowerCase("ru-RU")
    const fragments: ReactNode[] = []
    let cursor = 0
    let matchIndex = normalizedText.indexOf(normalizedQuery)

    while (matchIndex >= 0) {
        if (matchIndex > cursor) {
            fragments.push(text.slice(cursor, matchIndex))
        }
        fragments.push(
            <mark key={`${matchIndex}-${cursor}`} className="rounded bg-[#FFF1A8] px-0.5 text-inherit">
                {text.slice(matchIndex, matchIndex + normalizedQuery.length)}
            </mark>
        )
        cursor = matchIndex + normalizedQuery.length
        matchIndex = normalizedText.indexOf(normalizedQuery, cursor)
    }

    if (cursor < text.length) {
        fragments.push(text.slice(cursor))
    }

    return <>{fragments}</>
}

const formatPrice = (value?: number): string => {
    if (typeof value !== "number") {
        return "—"
    }

    return new Intl.NumberFormat("ru-RU", {
        maximumFractionDigits: 2,
    }).format(value)
}

const renderPrices = (prices: IFullMenuPrice[]) => {
    if (prices.length === 0) {
        return <span className="text-sm text-[#828282]">Цены не указаны</span>
    }

    return (
        <div className="flex flex-wrap gap-2">
            {prices.map((price, index) => (
                <span key={`${price.organization_id ?? "price"}-${index}`} className="rounded-full bg-[#F7F8FA] px-3 py-1 text-sm">
                    Цена: {formatPrice(price.price)} ₽
                </span>
            ))}
        </div>
    )
}

const ImageStrip = ({ images }: { images: IFullMenuImage[] }) => {
    const sortedImages = sortImages(images)

    if (sortedImages.length === 0) {
        return <span className="text-sm text-[#828282]">Изображений нет</span>
    }

    return (
        <div className="flex flex-wrap gap-2">
            {sortedImages.map((image, index) => (
                <div key={`${image.id ?? image.url ?? "image"}-${index}`} className="h-16 w-16 overflow-hidden rounded-lg bg-[#F7F8FA]">
                    {image.url ? (
                        <img src={image.url} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-[#828282]">—</div>
                    )}
                </div>
            ))}
        </div>
    )
}

const ItemThumbnail = ({
    images,
    title,
    isDisabled,
    onOpen,
}: {
    images: IFullMenuImage[]
    title: string
    isDisabled?: boolean
    onOpen: (title: string, images: IFullMenuImage[]) => void
}) => {
    const sortedImages = sortImages(images)
    const firstImage = sortedImages[0]
    const canOpen = sortedImages.length > 0

    return (
        <button
            type="button"
            disabled={!canOpen}
            onClick={() => canOpen && onOpen(title, sortedImages)}
            className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#F7F8FA] ${canOpen ? "cursor-zoom-in" : "cursor-default"} ${isDisabled ? "opacity-50" : ""}`}
            aria-label={canOpen ? `Открыть галерею: ${title}` : `Нет изображений: ${title}`}
        >
            {firstImage?.url ? (
                <img src={firstImage.url} alt="" className="h-full w-full object-cover" />
            ) : (
                <span className="flex h-full w-full items-center justify-center text-xs text-[#828282]">Нет фото</span>
            )}
        </button>
    )
}

const ImageCarousel = ({
    state,
    onChange,
    onClose,
}: {
    state: CarouselState | null
    onChange: (state: CarouselState) => void
    onClose: () => void
}) => {
    if (!state) {
        return null
    }

    const currentImage = state.images[state.index]
    const previousIndex = state.index === 0 ? state.images.length - 1 : state.index - 1
    const nextIndex = state.index === state.images.length - 1 ? 0 : state.index + 1

    return (
        <Modal isOpen={Boolean(state)} title={`Галерея: ${state.title}`} onRequestClose={onClose}>
            <div className="flex min-w-[640px] flex-col gap-4">
                <div className="flex min-h-[420px] items-center justify-center overflow-hidden rounded-xl bg-[#F7F8FA]">
                    {currentImage?.url ? (
                        <img src={currentImage.url} alt={`${state.title}, изображение ${state.index + 1}`} className="max-h-[65vh] max-w-full object-contain" />
                    ) : (
                        <span className="text-[#828282]">Изображение недоступно</span>
                    )}
                </div>
                <div className="flex items-center justify-between gap-3">
                    <button
                        type="button"
                        className="form_cancel_button !w-auto px-6"
                        disabled={state.images.length < 2}
                        onClick={() => onChange({ ...state, index: previousIndex })}
                    >
                        Назад
                    </button>
                    <span className="text-sm text-[#828282]">
                        {state.index + 1} из {state.images.length}
                    </span>
                    <button
                        type="button"
                        className="form_handle_button !w-auto px-6"
                        disabled={state.images.length < 2}
                        onClick={() => onChange({ ...state, index: nextIndex })}
                    >
                        Далее
                    </button>
                </div>
                {state.images.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-2">
                        {state.images.map((image, index) => (
                            <button
                                type="button"
                                key={`${image.id ?? image.url ?? "carousel"}-${index}`}
                                onClick={() => onChange({ ...state, index })}
                                className={`h-14 w-14 overflow-hidden rounded-lg border-2 bg-[#F7F8FA] ${index === state.index ? "border-color-dark" : "border-transparent"}`}
                                aria-label={`Показать изображение ${index + 1}`}
                            >
                                {image.url ? <img src={image.url} alt="" className="h-full w-full object-cover" /> : "—"}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    )
}

const DisabledBadge = ({ isDisabled }: { isDisabled?: boolean }) => {
    if (!isDisabled) {
        return <span className="rounded-full bg-[#E8F5E9] px-3 py-1 text-sm text-[#2E7D32]">Включено</span>
    }

    return <span className="rounded-full bg-[#FFF4F4] px-3 py-1 text-sm text-color-danger">Отключено</span>
}

const EntityActions = ({
    canOpenImages,
    isAvailabilityPending,
    isDisabled,
    onOpenImages,
    onOpenTranslations,
    onToggleAvailability,
}: {
    canOpenImages: boolean
    isAvailabilityPending: boolean
    isDisabled?: boolean
    onOpenImages?: () => void
    onOpenTranslations: () => void
    onToggleAvailability?: () => void
}) => (
    <div className="flex flex-wrap items-center gap-2">
        {canOpenImages && (
            <button type="button" onClick={onOpenImages} className="form_handle_button !h-9 !w-auto px-4">
                Изображения
            </button>
        )}
        <button type="button" onClick={onOpenTranslations} className="form_cancel_button !h-9 !w-auto px-4">
            Переводы
        </button>
        {onToggleAvailability && (
            <button
                type="button"
                onClick={onToggleAvailability}
                disabled={isAvailabilityPending}
                className={`${isDisabled ? "form_handle_button" : "form_cancel_button"} !h-9 !w-auto px-4 disabled:cursor-not-allowed disabled:opacity-50`}
            >
                {isAvailabilityPending ? "..." : isDisabled ? "Включить" : "Отключить"}
            </button>
        )}
    </div>
)

export const MenuTree = ({
    menuId,
    data,
    onRefresh,
    onOpenImages,
    onOpenTranslations,
}: MenuTreeProps) => {
    const [search, setSearch] = useState("")
    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})
    const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
    const [openModifiers, setOpenModifiers] = useState<Record<string, boolean>>({})
    const [pendingAvailability, setPendingAvailability] = useState<string | null>(null)
    const [carousel, setCarousel] = useState<CarouselState | null>(null)
    const normalizedSearch = normalizeSearchText(search)
    const isSearching = Boolean(normalizedSearch)

    const searchResult = useMemo<SearchResult>(() => {
        const result: SearchResult = {
            count: 0,
            categoryMatches: new Set<string>(),
            itemMatches: new Set<string>(),
            modifierMatches: new Set<string>(),
            visibleCategories: new Set<string>(),
            visibleItems: new Set<string>(),
        }

        if (!normalizedSearch) {
            return result
        }

        data.categories.forEach((category, categoryIndex) => {
            const categoryKey = getCategoryKey(category, categoryIndex)
            const isCategoryMatch = isTextMatch(category.name, normalizedSearch)
            if (isCategoryMatch) {
                result.categoryMatches.add(categoryKey)
                result.visibleCategories.add(categoryKey)
                result.count += 1
            }

            category.items.forEach((item, itemIndex) => {
                const itemKey = getItemKey(item, categoryKey, itemIndex)
                const isItemMatch = isTextMatch(item.name, normalizedSearch)
                let hasVisibleModifier = false

                if (isItemMatch) {
                    result.itemMatches.add(itemKey)
                    result.count += 1
                }

                item.sizes.forEach((size, sizeIndex) => {
                    size.modifier_groups.forEach((group, groupIndex) => {
                        sortModifiers(group.modifiers).forEach((modifier, modifierIndex) => {
                            if (!isTextMatch(modifier.name, normalizedSearch)) {
                                return
                            }

                            const modifierKey = getModifierKey(
                                modifier,
                                itemKey,
                                sizeIndex,
                                groupIndex,
                                modifierIndex
                            )
                            result.modifierMatches.add(modifierKey)
                            result.count += 1
                            hasVisibleModifier = true
                        })
                    })
                })

                if (isCategoryMatch || isItemMatch || hasVisibleModifier) {
                    result.visibleItems.add(itemKey)
                    result.visibleCategories.add(categoryKey)
                }
            })
        })

        return result
    }, [data.categories, normalizedSearch])

    const toggleCategory = (key: string) => {
        if (isSearching) {
            return
        }
        setOpenCategories((state) => ({ ...state, [key]: !(state[key] ?? true) }))
    }

    const toggleItem = (key: string) => {
        if (isSearching) {
            return
        }
        setOpenItems((state) => ({ ...state, [key]: !(state[key] ?? false) }))
    }

    const toggleModifier = (key: string) => {
        if (isSearching) {
            return
        }
        setOpenModifiers((state) => ({ ...state, [key]: !(state[key] ?? false) }))
    }

    const openCarousel = (title: string, images: IFullMenuImage[]) => {
        setCarousel({
            title,
            images: sortImages(images),
            index: 0,
        })
    }

    const handleToggleItem = async (item: IFullMenuItem) => {
        if (!item.item_id) {
            notify.error("У позиции нет идентификатора")
            return
        }

        const pendingKey = `item:${item.item_id}`

        try {
            setPendingAvailability(pendingKey)
            await updateRestaurantMenuItemAvailability(menuId, item.item_id, { is_disabled: !Boolean(item.is_disabled) })
            await onRefresh()
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        } finally {
            setPendingAvailability(null)
        }
    }

    const handleToggleModifier = async (modifier: IFullMenuModifier) => {
        if (!modifier.item_id) {
            notify.error("У модификатора нет идентификатора")
            return
        }

        const pendingKey = `modifier:${modifier.item_id}`

        try {
            setPendingAvailability(pendingKey)
            await updateRestaurantMenuModifierAvailability(menuId, modifier.item_id, { is_disabled: !Boolean(modifier.is_disabled) })
            await onRefresh()
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        } finally {
            setPendingAvailability(null)
        }
    }

    if (data.categories.length === 0) {
        return (
            <div className="info_panel">
                Меню ещё не синхронизировано с IIKO — позиций нет
            </div>
        )
    }

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-[#D9E3E1] bg-white p-4">
                    <label className="flex min-w-[280px] flex-1 flex-col gap-2">
                        <span className="text-sm font-medium uppercase text-[#828282]">Поиск по дереву</span>
                        <input
                            type="search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Категория, позиция или модификатор"
                            className="input_field"
                        />
                    </label>
                    <div className="pb-3 text-sm text-[#828282]" aria-live="polite">
                        {isSearching ? `Найдено: ${searchResult.count}` : "Введите текст для поиска"}
                    </div>
                </div>

                {isSearching && searchResult.count === 0 && (
                    <div className="info_panel" role="status">
                        ничего не найдено
                    </div>
                )}

                {(!isSearching || searchResult.count > 0) && data.categories.map((category, categoryIndex) => {
                    const categoryKey = getCategoryKey(category, categoryIndex)
                    if (isSearching && !searchResult.visibleCategories.has(categoryKey)) {
                        return null
                    }

                    const isCategoryOpen = isSearching
                        ? true
                        : openCategories[categoryKey] ?? true

                    return (
                        <div key={categoryKey} className="rounded-lg border border-[#D9E3E1] bg-white">
                            <div className="flex items-start justify-between gap-4 p-4">
                                <button
                                    type="button"
                                    onClick={() => toggleCategory(categoryKey)}
                                    className="min-w-[240px] flex-1 text-left"
                                    aria-expanded={isCategoryOpen}
                                >
                                    <div className="text-lg font-medium">
                                        <HighlightedText text={category.name || "Без названия"} query={search} />
                                    </div>
                                    {category.description && <div className="mt-1 text-sm text-[#828282]">{category.description}</div>}
                                </button>
                                <EntityActions
                                    canOpenImages={false}
                                    isAvailabilityPending={false}
                                    onOpenTranslations={() => category.id && onOpenTranslations({
                                        entityType: "category",
                                        entityId: category.id,
                                        title: category.name || "Категория",
                                        translations: category.translations,
                                    })}
                                />
                            </div>

                            {isCategoryOpen && (
                                <div className="flex flex-col gap-3 border-t border-[#D9E3E1] p-4">
                                    {category.items.length === 0 && (
                                        <div className="text-sm text-[#828282]">В категории нет товаров</div>
                                    )}

                                    {category.items.map((item, itemIndex) => {
                                        const itemKey = getItemKey(item, categoryKey, itemIndex)
                                        if (isSearching && !searchResult.visibleItems.has(itemKey)) {
                                            return null
                                        }

                                        const isItemOpen = isSearching
                                            ? searchResult.itemMatches.has(itemKey) || Array.from(searchResult.modifierMatches).some((key) => key.startsWith(`${itemKey}:`))
                                            : openItems[itemKey] ?? false
                                        const itemTitle = item.name || "Без названия"

                                        return (
                                            <div key={itemKey} className="rounded-lg border border-[#D9E3E1] p-4">
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <ItemThumbnail
                                                        images={item.images}
                                                        title={itemTitle}
                                                        isDisabled={item.is_disabled}
                                                        onOpen={openCarousel}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleItem(itemKey)}
                                                        className={`min-w-[200px] flex-1 text-left ${item.is_disabled ? "opacity-50" : ""}`}
                                                        aria-expanded={isItemOpen}
                                                    >
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-base font-medium">
                                                                <HighlightedText text={itemTitle} query={search} />
                                                            </span>
                                                            <DisabledBadge isDisabled={item.is_disabled} />
                                                            <span className="text-sm text-[#828282]">{isItemOpen ? "Свернуть" : "Подробнее"}</span>
                                                        </div>
                                                    </button>
                                                    <EntityActions
                                                        canOpenImages={Boolean(item.item_id)}
                                                        isAvailabilityPending={pendingAvailability === `item:${item.item_id}`}
                                                        isDisabled={item.is_disabled}
                                                        onOpenImages={() => item.item_id && onOpenImages({
                                                            itemId: item.item_id,
                                                            title: itemTitle,
                                                        })}
                                                        onOpenTranslations={() => item.item_id && onOpenTranslations({
                                                            entityType: "item",
                                                            entityId: item.item_id,
                                                            title: itemTitle,
                                                            translations: item.translations,
                                                        })}
                                                        onToggleAvailability={() => handleToggleItem(item)}
                                                    />
                                                </div>

                                                {isItemOpen && (
                                                    <div className={`mt-4 flex flex-col gap-4 border-t border-[#D9E3E1] pt-4 ${item.is_disabled ? "opacity-50" : ""}`}>
                                                        {item.description && <div className="text-sm text-[#828282]">{item.description}</div>}
                                                        <div>
                                                            <div className="mb-2 text-sm font-medium text-[#828282]">Изображения</div>
                                                            <ImageStrip images={item.images} />
                                                        </div>

                                                        <div>
                                                            <div className="mb-2 text-sm font-medium text-[#828282]">Размеры и цены</div>
                                                            <div className="flex flex-col gap-2">
                                                                {item.sizes.length === 0 && <span className="text-sm text-[#828282]">Размеры не указаны</span>}
                                                                {item.sizes.map((size, sizeIndex) => (
                                                                    <div key={`${size.size_id ?? size.sku ?? "size"}-${sizeIndex}`} className="rounded-lg bg-[#F7F8FA] p-3">
                                                                        <div className="mb-2 font-medium">{size.size_name || size.size_code || "Размер"}</div>
                                                                        {renderPrices(size.prices)}
                                                                        {size.modifier_groups.length > 0 && (
                                                                            <div className="mt-3 flex flex-col gap-3">
                                                                                {size.modifier_groups.map((group, groupIndex) => (
                                                                                    <div key={`${group.item_group_id ?? group.sku ?? "group"}-${groupIndex}`} className="rounded-lg bg-white p-3">
                                                                                        <div className="font-medium">{group.name || "Группа модификаторов"}</div>
                                                                                        {group.description && <div className="mt-1 text-sm text-[#828282]">{group.description}</div>}
                                                                                        <div className="mt-3 flex flex-col gap-2">
                                                                                            {sortModifiers(group.modifiers).map((modifier, modifierIndex) => {
                                                                                                const modifierKey = getModifierKey(
                                                                                                    modifier,
                                                                                                    itemKey,
                                                                                                    sizeIndex,
                                                                                                    groupIndex,
                                                                                                    modifierIndex
                                                                                                )
                                                                                                const isModifierOpen = isSearching
                                                                                                    ? searchResult.modifierMatches.has(modifierKey)
                                                                                                    : openModifiers[modifierKey] ?? false
                                                                                                const modifierTitle = modifier.name || "Модификатор"

                                                                                                return (
                                                                                                    <div key={modifierKey} className="rounded-lg border border-[#D9E3E1] p-3">
                                                                                                        <div className="flex flex-wrap items-center gap-3">
                                                                                                            <ItemThumbnail
                                                                                                                images={modifier.images}
                                                                                                                title={modifierTitle}
                                                                                                                isDisabled={modifier.is_disabled}
                                                                                                                onOpen={openCarousel}
                                                                                                            />
                                                                                                            <button
                                                                                                                type="button"
                                                                                                                onClick={() => toggleModifier(modifierKey)}
                                                                                                                className={`min-w-[180px] flex-1 text-left ${modifier.is_disabled ? "opacity-50" : ""}`}
                                                                                                                aria-expanded={isModifierOpen}
                                                                                                            >
                                                                                                                <div className="flex flex-wrap items-center gap-2">
                                                                                                                    <span className="font-medium">
                                                                                                                        <HighlightedText text={modifierTitle} query={search} />
                                                                                                                    </span>
                                                                                                                    <DisabledBadge isDisabled={modifier.is_disabled} />
                                                                                                                    <span className="text-sm text-[#828282]">{isModifierOpen ? "Свернуть" : "Подробнее"}</span>
                                                                                                                </div>
                                                                                                            </button>
                                                                                                            <EntityActions
                                                                                                                canOpenImages={Boolean(modifier.item_id)}
                                                                                                                isAvailabilityPending={pendingAvailability === `modifier:${modifier.item_id}`}
                                                                                                                isDisabled={modifier.is_disabled}
                                                                                                                onOpenImages={() => modifier.item_id && onOpenImages({
                                                                                                                    itemId: modifier.item_id,
                                                                                                                    title: modifierTitle,
                                                                                                                })}
                                                                                                                onOpenTranslations={() => modifier.item_id && onOpenTranslations({
                                                                                                                    entityType: "modifier",
                                                                                                                    entityId: modifier.item_id,
                                                                                                                    title: modifierTitle,
                                                                                                                    translations: modifier.translations,
                                                                                                                })}
                                                                                                                onToggleAvailability={() => handleToggleModifier(modifier)}
                                                                                                            />
                                                                                                        </div>
                                                                                                        {isModifierOpen && (
                                                                                                            <div className={`mt-3 flex flex-col gap-3 border-t border-[#D9E3E1] pt-3 ${modifier.is_disabled ? "opacity-50" : ""}`}>
                                                                                                                {modifier.description && <div className="text-sm text-[#828282]">{modifier.description}</div>}
                                                                                                                {renderPrices(modifier.prices)}
                                                                                                                <ImageStrip images={modifier.images} />
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                )
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            <ImageCarousel
                state={carousel}
                onChange={setCarousel}
                onClose={() => setCarousel(null)}
            />
        </>
    )
}
